import React from "react";
import { AlertCircle, RefreshCw, ArrowLeft, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT } from "../data/cruiseData";

/* Resilient error boundary for booking/checkout pages.
   Catches any render-time crash (e.g. an unguarded `evt.name` while data is loading)
   and shows a friendly recovery card instead of a white screen , protects conversions
   for guests on flaky connections. */
class CheckoutErrorBoundary extends React.Component {
 constructor(props) {
  super(props);
  this.state = { hasError: false, error: null };
 }

 static getDerivedStateFromError(error) {
  return { hasError: true, error };
 }

 componentDidCatch(error, info) {
  // Surface to console so DevTools / Sentry / Logflare can pick it up later.
  // eslint-disable-next-line no-console
  console.error("[CheckoutErrorBoundary]", error, info?.componentStack);
 }

 handleRetry = () => {
  this.setState({ hasError: false, error: null });
  // Hard reload as a last resort to flush any bad state.
  if (typeof window !== "undefined") window.location.reload();
 };

 render() {
  if (!this.state.hasError) return this.props.children;

  const phone = CONTACT?.phone1 || "1300 100 502";
  const phoneHref = `tel:${phone.replace(/\s+/g, "")}`;

  return (
   <main className="min-h-screen relative overflow-hidden bg-[#06060A] text-white flex items-center justify-center px-5 py-20" data-testid="checkout-error-boundary">
    <div className="absolute inset-0 pointer-events-none">
     <div className="absolute top-1/3 left-1/4 w-[36rem] h-[36rem] rounded-full blur-3xl animate-pulse" style={{ backgroundColor: "rgba(212,175,55,0.10)" }} />
     <div className="absolute bottom-0 right-1/4 w-[28rem] h-[28rem] rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s", backgroundColor: "rgba(225,29,72,0.10)" }} />
    </div>
    <div className="relative max-w-xl w-full text-center rounded-3xl ring-1 ring-amber-300/25 bg-gradient-to-br from-amber-300/10 via-white/[0.04] to-transparent backdrop-blur-md p-8 lg:p-12">
     <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-300/15 ring-1 ring-amber-300/40 mb-6">
      <AlertCircle className="w-8 h-8 text-amber-300" strokeWidth={1.7} />
     </div>
     <h1 className="font-black tracking-tight leading-tight text-3xl lg:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>
      Something hiccuped on our end.
     </h1>
     <p className="text-white/72 text-base lg:text-lg mt-4 leading-relaxed">
      The booking page didn't load properly. Your tickets <strong className="text-white">were not charged</strong>. Refresh to try again, or give the Magic team a quick call , we'll book you in over the phone in under 2 minutes.
     </p>

     <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
      <button
       onClick={this.handleRetry}
       className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase text-[#0A0A0A] bg-amber-300 hover:bg-amber-200 shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] transition-all hover:scale-[1.03]"
       data-testid="checkout-error-retry"
      >
       <RefreshCw className="w-4 h-4" /> Refresh to retry
      </button>
      <a
       href={phoneHref}
       className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-amber-300/60 hover:text-amber-300 transition-all text-white"
       data-testid="checkout-error-call"
      >
       <Phone className="w-4 h-4" /> Call {phone}
      </a>
     </div>

     <Link
      to="/"
      className="inline-flex items-center gap-2 text-white/55 hover:text-white text-xs uppercase tracking-[0.28em] font-bold mt-8 transition-colors"
      data-testid="checkout-error-home"
     >
      <ArrowLeft className="w-3.5 h-3.5" /> Back to home
     </Link>
    </div>
   </main>
  );
 }
}

export default CheckoutErrorBoundary;
